// src/staffAdmin/index.js — the staff-administration UI cluster (moved
// VERBATIM from admin.html, FE-breakup slice 7; bundled to dist/staffAdmin.js):
// staff row rendering, the AUDITED PIN-reveal client (HIGH-2 flow — calls the
// worker's scope-checked, audit-logged endpoint; moved byte-identical), consent
// reset, persist-to-DB, add-staff modal, changelog, and the requests board.
//
// PAGE-GLOBAL STATE CONTRACT (declarations stay in admin.html): staffData,
// editingStaffId (WRITTEN cross-bundle by src/uploads/staff.js), changeLog,
// requestsData. Shared helpers stay inline and resolve by free name:
// changeTypeLabel, changeDesc, escapeHtml, showToast, getVisibleStaff,
// canEditStaff, populateReqStaffSelect, DB, dlog.
//
// Heavy cross-bundle callers (why every function below is window-bound):
// src/uploads/staff.js calls staffRowHtml, persistStaffData,
// updateStoreDropdown, hideRevealedPin, closeAddStaffModal, renderChangelog;
// src/uploads/budget.js calls persistStaffData; plus markup onclick/onchange
// and showAdmin/switchGroupSub init calls.

import { setHTML } from '../shared/set-html.js';   // v136.8: null-safe innerHTML for admin renders
import { coverAwareAreaStores } from '../shared/area-cover.js';   // v136.9: own area plus any area handed over for today

// ── STAFF RENDER ──────────────────────────────────────────────────────────
// v101.0: renderStaff → Uploads.staff.renderStaff
// v101.0: renderStaffGrouped → Uploads.staff.renderStaffGrouped
function staffRowHtml(s, today) {
  const initials = s.name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0,2).toUpperCase();
  const color = s.color || COLORS[s.id % COLORS.length];
  const roleBadge = s.role === 'store_manager' ? '<span class="badge badge-green">SM</span>' :
    s.role === 'Store in-charge' ? '<span class="badge badge-blue">IC</span>' : '';
  const pending = changeLog.find(c => c.staffId === s.id && !c.appliedAt && c.effectiveFrom > today);
  const pendingTag = pending ? '<span class="staff-pending">Change ' + pending.effectiveFrom + '</span>' : '';
  // v59: privacy-notice consent indicator. Small dot — green if acknowledged
  // current version, amber if outstanding. Tooltip shows when (or that it's pending).
  const ackOk = s.consent_version === (typeof CONSENT_VERSION !== 'undefined' ? CONSENT_VERSION : 'v1');
  const ackTip = ackOk
    ? 'Privacy notice acknowledged' + (s.consent_acknowledged_at ? ' on ' + new Date(s.consent_acknowledged_at).toLocaleDateString() : '')
    : 'Privacy notice not yet acknowledged';
  const ackDot = '<span title="' + ackTip + '" style="display:inline-block;width:8px;height:8px;border-radius:50%;background:' + (ackOk ? '#16a34a' : '#f59e0b') + ';margin-inline-start:6px;vertical-align:middle"></span>';
  const canEdit = canEditStaff(s);
  // v129.28 — 📈 opens the promotion matrix for this person. The bundle returns
  // '' for anyone who isn't HO or an area manager, so a store manager gets no
  // button at all rather than one that 403s. typeof-guarded like every other
  // cross-bundle call here: staffRowHtml also runs before dist/promotion.js has
  // parsed on a cold, cache-miss load.
  const promoBtn = (typeof Promotion !== 'undefined' && Promotion.buttonHtml) ? Promotion.buttonHtml(s.id) : '';
  return `<div class="staff-row">
    <!-- v129.23: the admin People list hand-rolled initials and never asked for
         a photo, so HO could SET a staff photo from this very screen (admin.html
         Avatars.openAdminSet) and never see it here. data-av-* is what the
         avatars sweep looks for; overflow:hidden clips the <img> it injects. -->
    <div class="staff-avatar" data-av-kind="staff" data-av-id="${s.id}" style="background:${color};overflow:hidden">${initials}</div>
    <div class="staff-info">
      <div class="staff-name"><span translate="no">${escapeHtml(s.name||'')}</span> ${roleBadge} ${pendingTag}${ackDot}</div>
      <div class="staff-meta"><span>${escapeHtml(s.role||'')}</span>${s.dayOff ? ' · Day off: <span>' + escapeHtml(s.dayOff) + '</span>' : ''}</div>
    </div>
    <div class="staff-actions">
      ${promoBtn}
      ${canEdit ? '<button class="btn btn-outline btn-sm" onclick="Uploads.staff.openStaffModal(' + s.id + ')">Edit</button>' : '<span style="font-size:11px;color:var(--tiny)">View only</span>'}
    </div>
  </div>`;
}

// v101.0: renderStaffTable → Uploads.staff.renderStaffTable
// ── STAFF MODALS ──────────────────────────────────────────────────────────
// v104.0: openStaffModal → Uploads.staff.openStaffModal
// v60: reveal a staff's current PIN, with audit logging on the server.
// Called from the "Reveal current PIN" button in the staff edit modal.
// The reveal is auto-hidden after 30s for shoulder-surfing safety.
let _revealHideTimer = null;
async function revealCurrentPin() {
  if (!editingStaffId) return;
  const btn = document.getElementById('sfRevealPinBtn');
  const result = document.getElementById('sfRevealPinResult');
  if (!btn || !result) return;
  // Confirm — the reveal is logged, make sure the admin knows
  if (!confirm('Reveal this PIN?\n\nThe reveal will be logged with your name and timestamp, visible to other admins in the Dev → PIN Reveals tab.')) return;

  btn.disabled = true;
  btn.textContent = 'Revealing…';
  try {
    const res = await DB.staff.revealPin(editingStaffId);
    if (!res || !res.pin) {
      showToast('No PIN is set for this staff.', 'error');
      btn.disabled = false;
      btn.textContent = '🔒 Reveal current PIN';
      return;
    }
    result.textContent = res.pin;
    result.style.display = '';
    btn.style.display = 'none';
    // Auto-hide after 30 seconds
    if (_revealHideTimer) clearTimeout(_revealHideTimer);
    _revealHideTimer = setTimeout(hideRevealedPin, 30000);
  } catch(e) {
    console.error('[reveal-pin] failed:', e.message);
    showToast('Reveal failed: ' + e.message, 'error');
    btn.disabled = false;
    btn.textContent = '🔒 Reveal current PIN';
  }
}
function hideRevealedPin() {
  const btn = document.getElementById('sfRevealPinBtn');
  const result = document.getElementById('sfRevealPinResult');
  if (result) { result.textContent = ''; result.style.display = 'none'; }
  if (btn) { btn.disabled = false; btn.textContent = '🔒 Reveal current PIN'; btn.style.display = ''; }
  if (_revealHideTimer) { clearTimeout(_revealHideTimer); _revealHideTimer = null; }
}

// v61: render the privacy-notice status for the staff currently in the
// edit modal. Shows "✅ Acknowledged on <date>" or "🟠 Not yet acknowledged".
// Reset button only enabled when there's actually something to reset.
// v101.0: renderStaffConsentStatus → Uploads.staff.renderStaffConsentStatus
// v61: reset privacy notice acknowledgement for one staff. Used to clear
// test acknowledgements without bulk-resetting everyone.
async function resetThisStaffConsent() {
  if (!editingStaffId) return;
  const s = staffData.find(x => x.id === editingStaffId);
  if (!s) return;
  if (!confirm('Reset privacy notice acknowledgement for ' + s.name + '?\n\nThey will be asked to acknowledge again on their next clock-in.')) return;
  const btn = document.getElementById('sfResetConsentBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Resetting…'; }
  try {
    await DB.staff.resetConsent(editingStaffId);
    // Update local cache
    s.consent_version = null;
    s.consent_acknowledged_at = null;
    s.consent_lang = null;
    Uploads.staff.renderStaffConsentStatus(s);
    showToast('Acknowledgement reset for ' + s.name, 'success');
    // Refresh directory dot indicator
    Uploads.staff.renderStaff();  // v101.0: typeof guard removed — direct call after staff CRUD moved to module
  } catch(e) {
    console.error('[reset-consent-one] failed:', e.message);
    showToast('Reset failed: ' + e.message, 'error');
    if (btn) { btn.disabled = false; btn.textContent = '🔄 Reset acknowledgement'; }
  }
}

// v104.0: closeStaffModal → Uploads.staff.closeStaffModal
// v101.0: saveStaff → Uploads.staff.saveStaff
// ── PERSIST STAFF — write individual record to DB ─────────────────────────
// Called after edits. Writes the changed staff member to DB.
// Also keeps localStorage cache as offline fallback for index.html.
function persistStaffData(changedId) {
  // localStorage cache for offline fallback. Note: pin field omitted from the
  // cache entirely — v60 worker no longer returns it, so the cache can't be
  // accurate. index.html's clock-in path uses /api/verify-pin server-side.
  try {
    const compact = staffData.map(s => ({
      id: s.id, name: s.name, role: s.role, store: s.store,
      area: s.area, country: s.country, dayOff: s.dayOff || null,
      color: s.color, active: s.active !== false,
      toilBalance: s.toilBalance || 0, contractedHours: s.contractedHours || 48,
    }));
    localStorage.setItem('db_staff', JSON.stringify(compact));
  } catch(e) {}
  // Write to database
  if (changedId) {
    const s = staffData.find(x => x.id === changedId);
    if (s) {
      // v60: only include `pin` in the patch when we actually have a real,
      // typed value to write. Previously this fell through to '000000' every
      // time because s.pin is undefined in v60 (worker strips it from /api/staff).
      // Result: every save was wiping the PIN.
      // v129.14 — s.area can carry a legacy area NAME (mapStaffFromDB's
      // area_id || area_name || area fallback chain); writing it straight
      // back into area_id would PERSIST the corruption ('Qatar' failing every
      // === '3' comparison forever). Normalise to a known area id, else
      // derive from the store, else pass through (the nightly canary flags it).
      const normArea = (function () {
        const a = s.area == null ? '' : String(s.area);
        try {
          const areas = (typeof ALL_AREAS !== 'undefined' && ALL_AREAS) || [];
          if (areas.some((r) => String(r.id) === a)) return a;
          const byName = areas.find((r) => r && r.name === a);
          if (byName) return String(byName.id);
          if (typeof getStoreArea === 'function') {
            const fromStore = getStoreArea(s.store);
            if (fromStore) return String(fromStore);
          }
        } catch (e) { /* fall through */ }
        return a || null;
      })();
      const patch = {
        name: s.name, role: s.role, store: s.store, area_id: normArea,
        country: s.country, color: s.color, day_off: s.dayOff || null,
        active: s.active !== false,
        toil_balance: s.toilBalance || 0, contracted_hours: s.contractedHours || 48,
      };
      // v136.6: a PIN is exactly 6 digits (no zero padding: it turned 4728 into 004728 and locked the person out).
      if (s.pin && /^\d{6}$/.test(s.pin)) {
        patch.pin = s.pin;
      }
      // v59: emp_code is required + UNIQUE on the DB. Always write it.
      // Server returns a 409 if it dupes — surfaced via the catch below.
      if (s.empCode) patch.emp_code = s.empCode;
      DB.staff.update(changedId, patch).catch(e => {
        console.error('[DB] Staff update failed:', e.message);
        showToast('⚠️ Save failed — changes may not persist. See console for details.', 'error');
      });
    }
  }
}

// v101.0: archiveStaff → Uploads.staff.archiveStaff
// v133.59: cancelPendingChange removed with the staff form's "Cancel scheduled change" button: future-dated
// changes are refused at save (#1758), so there is never a scheduled change to cancel.

function openAddStaffModal() {
  document.getElementById('newSfName').value = '';
  document.getElementById('newSfEmpCode').value = '';
  document.getElementById('newSfRole').value = 'Sales Assistant';
  // v76: clear PIN field — never inherit from a previous open
  const pinEl = document.getElementById('newSfPin');
  if (pinEl) pinEl.value = '';
  updateNewStoreDropdown();
  document.getElementById('addStaffModal').classList.add('open');
}
function closeAddStaffModal() { document.getElementById('addStaffModal').classList.remove('open'); }

// v76: PIN is now required at staff creation (was previously hardcoded to
// '000000' which was a security hole — anyone who knew the system could clock
// in as a brand-new staff member at any store). Admin must set the PIN
// explicitly here, and it's checked for in-store duplicates the same way
// the staff-edit flow does (v61 pattern).
// v104.0: saveNewStaff → Uploads.staff.saveNewStaff
// Store dropdown helpers
function updateStoreDropdown() {
  const area = document.getElementById('sfArea')?.value;
  const sel = document.getElementById('sfStore');
  if (!sel) return;
  const areaStores = area ? (AREA_STORE_MAP[area] || ALL_STORES) : ALL_STORES;
  // Always include Retail Relief Managers as an option
  const stores = [...new Set([...areaStores, 'Retail Relief Managers'])].sort();
  sel.innerHTML = '';
  stores.forEach(s => { const o = document.createElement('option'); o.value = s; o.textContent = s; o.setAttribute('translate', 'no'); sel.appendChild(o); });
}
function updateNewStoreDropdown() {
  const area = document.getElementById('newSfArea')?.value;
  const sel = document.getElementById('newSfStore');
  if (!sel) return;
  const areaStores = area ? (AREA_STORE_MAP[area] || ALL_STORES) : ALL_STORES;
  const stores = [...new Set([...areaStores, 'Retail Relief Managers'])].sort();
  sel.innerHTML = '';
  stores.forEach(s => { const o = document.createElement('option'); o.value = s; o.textContent = s; o.setAttribute('translate', 'no'); sel.appendChild(o); });
}

// ── CHANGELOG ────────────────────────────────────────────────────────────
function renderChangelog() {
  const today = todayDateStr();
  const pending = changeLog.filter(c => !c.appliedAt && c.effectiveFrom > today).sort((a,b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  const past = changeLog.filter(c => c.appliedAt || c.effectiveFrom <= today).sort((a,b) => (b.createdAt||'').localeCompare(a.createdAt||'')).slice(0,30);

  // v136.8: setHTML no-ops when the box is not on screen (it used to throw and stop the render)
  if (!pending.length) {
    setHTML('pendingChangelog', '<div style="color:var(--sub);font-size:13px;padding:8px 0">No pending changes.</div>');
  } else {
    setHTML('pendingChangelog', pending.map(c => {
      const s = staffData.find(x => x.id === c.staffId);
      return `<div class="changelog-item">
        <div class="changelog-dot pending"></div>
        <div class="changelog-info">
          <div class="changelog-desc"><span translate="no">${escapeHtml(s?.name || '?')}</span>: ${changeTypeLabel(c.type)}</div>
          <div class="changelog-meta">Effective ${c.effectiveFrom} · Scheduled by <span translate="no">${escapeHtml(c.scheduledBy||'')}</span> · ${c.createdAt?.slice(0,10)||''}</div>
          <div class="pending-transfer">${escapeHtml(changeDesc(c))}</div>
        </div>
        <button class="btn btn-outline btn-sm" onclick="cancelChangeById('${c.id}')" style="color:var(--red);border-color:var(--red);flex-shrink:0">Cancel</button>
      </div>`;
    }).join(''));
  }

  if (!past.length) {
    setHTML('pastChangelog', '<div style="color:var(--sub);font-size:13px;padding:8px 0">No history yet.</div>');
  } else {
    setHTML('pastChangelog', past.map(c => {
      const s = staffData.find(x => x.id === c.staffId);
      return `<div class="changelog-item">
        <div class="changelog-dot"></div>
        <div class="changelog-info">
          <div class="changelog-desc"><span translate="no">${escapeHtml(s?.name || '?')}</span>: ${changeTypeLabel(c.type)}</div>
          <div class="changelog-meta">Applied ${c.appliedAt?.slice(0,10)||c.effectiveFrom} · <span translate="no">${escapeHtml(c.scheduledBy||'')}</span></div>
          <div style="font-size:12px;color:var(--sub)">${escapeHtml(changeDesc(c))}</div>
        </div>
      </div>`;
    }).join(''));
  }
}

function cancelChangeById(id) {
  const idx = changeLog.findIndex(c => String(c.id) === String(id));
  if (idx > -1) { changeLog.splice(idx, 1); showToast('Change cancelled','success'); }
  renderChangelog(); renderDashboard(); updateChangesBadge();
}

// ── REQUESTS ─────────────────────────────────────────────────────────────
// Leave requests (docs/leave-requests-plan.md): this was a placeholder board that only changed requestsData in the
// page (approving fed nothing into the rota, and a reload lost it). The real board is LeaveRequestsAdmin
// (src/leaveRequestsAdmin), drawn from the server; these names stay because admin.html and showAdmin call them.
function renderRequests() {
  const list = document.getElementById('reqList');
  if (!list) return;   // v136.8: null-safe (the box may not be on screen)
  if (typeof LeaveRequestsAdmin !== 'undefined' && LeaveRequestsAdmin.render) { LeaveRequestsAdmin.render(); return; }
  list.innerHTML = '<div class="card" style="text-align:center;padding:40px;color:var(--sub)"><span>Leave requests could not load. Reload the page.</span></div>';
}
function openReqModal() { if (typeof LeaveRequestsAdmin !== 'undefined' && LeaveRequestsAdmin.openRaise) LeaveRequestsAdmin.openRaise(); }
function closeReqModal() { document.getElementById('reqModal').classList.remove('open'); }
function saveRequest() { if (typeof LeaveRequestsAdmin !== 'undefined' && LeaveRequestsAdmin.saveRaise) LeaveRequestsAdmin.saveRaise(); }

// ── AMANAH STAFF CHANGES TO REVIEW (READ-ONLY) ───────────────────────────
// Shows WHAT the captured Amanah staff push (in ingest_inbox) would change vs
// current staff — moves, role/hours changes, leavers, new joiners — so the Ops
// Manager (admin/manager access, NOT the dev console) can eyeball it in the
// People > Directory tab.
//
// SCOPE: REVIEW-ONLY. It fetches the read-only GET /api/admin/ingest/staff-diff
// and renders it. There is deliberately NO approve/apply button here — applying
// these changes to `staff` arrives in a later update (PR2). Fetch pattern mirrors
// the other admin reads: DB.getToken() + DB.workerUrl() + Authorization Bearer,
// never a relative path.
async function loadStaffChanges() {
  const el = document.getElementById('staffChangesContent');
  if (!el) return;
  el.innerHTML = '<div style="font-size:13px;color:var(--sub);padding:8px 2px">Loading staff changes...</div>';
  try {
    const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
    const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    const headers = {};
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const res = await fetch(base + '/api/admin/ingest/staff-diff', { cache: 'no-store', headers: headers });
    const payload = await res.json().catch(function () { return {}; });
    if (!res.ok || !payload || payload.ok === false) {
      el.innerHTML = '<span style="color:var(--red);font-size:13px">Could not load staff changes: ' +
        escapeHtml((payload && (payload.reason || payload.error)) || ('HTTP ' + res.status)) + '</span>';
      return;
    }
    el.innerHTML = renderStaffChanges(payload);
  } catch (e) {
    el.innerHTML = '<span style="color:var(--red);font-size:13px">Could not load staff changes: ' +
      escapeHtml((e && e.message) || String(e)) + '</span>';
  }
}

// APPROVE & APPLY the reviewed Amanah staff changes. POSTs the admin-only
// /api/admin/ingest/staff-apply, which recomputes the diff SERVER-SIDE and writes
// the exact minimal changes to LIVE staff + shifts (name overwrite, hours, leaver,
// store move with shift pin/delete, new joiner). Role is never written. A confirm()
// summarises the counts first; the result is toasted + the diff re-loaded.
//
// _lastStaffChanges caches the last rendered diff so the confirm() can summarise
// counts without a second fetch (the server recomputes anyway — this is UI-only).
let _lastStaffChanges = null;

// POST /api/admin/ingest/staff-apply and report the result. `selection` (optional)
// is an array of { emp_code, category } — when present the server applies ONLY those
// recomputed changes; when absent/empty it applies everything. Shared by the
// approve-all and approve-selected buttons.
async function _postStaffApply(selection) {
  try {
    const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
    const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const opts = { method: 'POST', headers: headers, cache: 'no-store' };
    if (Array.isArray(selection) && selection.length) opts.body = JSON.stringify({ selection: selection });
    const res = await fetch(base + '/api/admin/ingest/staff-apply', opts);
    const payload = await res.json().catch(function () { return {}; });
    if (!res.ok || !payload || payload.ok === false) {
      showToast('Apply failed: ' + ((payload && (payload.reason || payload.error)) || ('HTTP ' + res.status)), 'error');
      return;
    }
    const skipped = Array.isArray(payload.skipped) ? payload.skipped.length : 0;
    const bits = [];
    if (payload.staffUpdated) bits.push(payload.staffUpdated + ' updated');
    if (payload.staffCreated) bits.push(payload.staffCreated + ' created');
    if (payload.shiftsRepinned) bits.push(payload.shiftsRepinned + ' shifts pinned');
    if (payload.shiftsDeleted) bits.push(payload.shiftsDeleted + ' shifts removed');
    if (skipped) bits.push(skipped + ' skipped');
    showToast('Applied: ' + (bits.length ? bits.join(', ') : 'no changes'), skipped ? '' : 'success');
    if (window.loadStaffChanges) window.loadStaffChanges();
  } catch (e) {
    showToast('Apply failed: ' + ((e && e.message) || String(e)), 'error');
  }
}

// Approve & apply ALL reviewed changes (no selection → server applies everything).
async function applyStaffChanges() {
  const d = _lastStaffChanges || {};
  const c = d.counts || {};
  const moves = c.moved || 0;
  const joiners = c.new_joiner || 0;
  const names = c.name_changed || 0;
  const hours = c.hours_changed || 0;
  const leavers = c.deactivated || 0;
  const safe = joiners + names + hours;   // "Approve all" applies SAFE changes ONLY
  const destructive = moves + leavers;    // store moves + removals are NEVER bulk-applied
  if (safe === 0 && destructive === 0) { showToast('Nothing to apply', ''); return; }
  if (safe === 0) {
    showToast('Only store moves / removals to review — tick each one and use "Approve selected".', '');
    return;
  }
  const parts = [];
  if (joiners) parts.push(joiners + ' new joiner' + (joiners === 1 ? '' : 's'));
  if (names) parts.push(names + ' name change' + (names === 1 ? '' : 's'));
  if (hours) parts.push(hours + ' hours change' + (hours === 1 ? '' : 's'));
  let msg = 'Apply ' + safe + ' safe update' + (safe === 1 ? '' : 's') + ' to LIVE records?\n\n' +
    parts.join(', ') + '.\n\n' +
    'New joiners are created active with NO PIN (they cannot clock in until a PIN is set). ' +
    'Role changes are NOT applied.';
  if (destructive) {
    const dParts = [];
    if (moves) dParts.push(moves + ' store move' + (moves === 1 ? '' : 's'));
    if (leavers) dParts.push(leavers + ' removal' + (leavers === 1 ? '' : 's'));
    msg += '\n\n⚠ ' + dParts.join(' + ') + ' are NOT included here — tick each one and use ' +
      '"Approve selected" to confirm those by name (Amanah may be out of date).';
  }
  msg += '\n\nThis cannot be undone from here.';
  if (!confirm(msg)) return;
  await _postStaffApply(null);
}

// Approve & apply ONLY the ticked change rows. Reads the checked checkboxes in the
// Amanah card, builds the { emp_code, category } selection, and POSTs it. Role rows
// are never checkable (flag-only); unknown/ambiguous rows are informational.
async function applySelectedStaffChanges() {
  const boxes = document.querySelectorAll('.amanah-change-cb:checked');
  const selection = [];
  const destructive = [];
  boxes.forEach(function (b) {
    const emp = b.getAttribute('data-emp');
    const cat = b.getAttribute('data-cat');
    if (!emp || !cat) return;
    selection.push({ emp_code: emp, category: cat });
    // Any move / removal gets named in a stronger confirmation (Amanah may be stale).
    if (cat === 'moved' || cat === 'deactivated') {
      const nm = b.getAttribute('data-name') || emp;
      destructive.push('  • ' + nm + ' — ' + (cat === 'moved'
        ? 'MOVE store (deletes their future shifts at the old store)'
        : 'REMOVE (mark as LEFT THE BUSINESS)'));
    }
  });
  if (!selection.length) { showToast('Select at least one change to apply', ''); return; }
  let msg = 'Apply ' + selection.length + ' selected change' + (selection.length === 1 ? '' : 's') + ' to LIVE records?\n\n';
  if (destructive.length) {
    msg += '⚠ CONFIRM these moves / removals — Amanah can be out of date, so only proceed if this is genuinely correct:\n' +
      destructive.join('\n') + '\n\n';
  }
  msg += 'Only the ticked rows are applied. New joiners are created active with NO PIN. ' +
    'Role changes are NOT applied. This cannot be undone from here.';
  if (!confirm(msg)) return;
  await _postStaffApply(selection);
}

// Select-all / none toggle for the Amanah change checkboxes.
function toggleAllStaffChanges(master) {
  const on = !!(master && master.checked);
  document.querySelectorAll('.amanah-change-cb').forEach(function (b) { b.checked = on; });
  updateAmanahSelectedCount();
}

// Keep the "Approve selected (N)" button in step with what's actually ticked.
// N is the live count of checked change rows (not the total applyable count).
function updateAmanahSelectedCount() {
  const n = document.querySelectorAll('.amanah-change-cb:checked').length;
  const el = document.getElementById('amanahSelCount');
  if (el) el.textContent = n;
}

// Build the review HTML from { changes, newJoiners, unknownStores, ambiguous, counts }.
// Grouped, human-readable lines with a count summary + a "nothing to review"
// empty state. The Approve & apply button + the caution flags are added at the end.
function renderStaffChanges(data) {
  _lastStaffChanges = data;
  const changes = Array.isArray(data.changes) ? data.changes : [];
  const newJoiners = Array.isArray(data.newJoiners) ? data.newJoiners : [];
  const unknownStores = Array.isArray(data.unknownStores) ? data.unknownStores : [];
  const ambiguous = Array.isArray(data.ambiguous) ? data.ambiguous : [];
  const c = data.counts || {};

  const total = changes.length + newJoiners.length + unknownStores.length + ambiguous.length;
  if (total === 0) {
    return '<div style="font-size:13px;color:var(--sub);padding:10px 2px">' +
      'Nothing to review - the latest Amanah push matches current staff.' +
    '</div>';
  }

  const who = function (row) {
    const nm = escapeHtml(row.name || '(no name)');
    const ec = row.emp_code != null ? (' <span translate="no" style="color:var(--sub)">(' + escapeHtml(String(row.emp_code)) + ')</span>') : '';
    return '<strong translate="no">' + nm + '</strong>' + ec;
  };
  // A per-change checkbox for line-level approval. Only APPLYABLE categories get one
  // (moved / name_changed / hours_changed / deactivated / new_joiner). Its
  // data-emp / data-cat drive the { emp_code, category } selection posted to the
  // apply. Role / unknown / ambiguous rows are informational and get no checkbox.
  const cb = function (empCode, category, name) {
    const emp = empCode != null ? escapeHtml(String(empCode)) : '';
    const nm = name != null ? escapeHtml(String(name)) : '';
    return '<input type="checkbox" class="amanah-change-cb" onchange="updateAmanahSelectedCount()" data-emp="' + emp + '" data-cat="' + escapeHtml(category) + '" data-name="' + nm + '" style="margin-inline-end:8px;vertical-align:middle">';
  };
  const val = function (v) {
    if (v === null || v === undefined || v === '') return '<em style="color:var(--sub)">(blank)</em>';
    return '<span translate="no">' + escapeHtml(String(v)) + '</span>';
  };
  const section = function (icon, label, lines) {
    if (!lines.length) return '';
    return '<div style="margin-bottom:12px">' +
      '<div style="font-size:11px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">' +
        icon + ' <span>' + escapeHtml(label) + '</span> (' + lines.length + ')</div>' +
      '<ul style="margin:0;padding-inline-start:18px">' + lines.join('') + '</ul>' +
    '</div>';
  };

  const moved = changes.filter(function (x) { return x.category === 'moved'; })
    .map(function (x) { return '<li style="font-size:13px;line-height:1.6">' + cb(x.emp_code, 'moved', x.name) + who(x) + ' &middot; ' + val(x.from) + ' &rarr; ' + val(x.to) + '</li>'; });
  const nameChanged = changes.filter(function (x) { return x.category === 'name_changed'; })
    .map(function (x) { return '<li style="font-size:13px;line-height:1.6">' + cb(x.emp_code, 'name_changed', x.name) + who(x) + ' &middot; ' + val(x.from) + ' &rarr; ' + val(x.to) + '</li>'; });
  const roleChanged = changes.filter(function (x) { return x.category === 'role_changed'; })
    .map(function (x) { return '<li style="font-size:13px;line-height:1.6">' + who(x) + ' &middot; ' + val(x.from) + ' &rarr; ' + val(x.to) + '</li>'; });
  const hoursChanged = changes.filter(function (x) { return x.category === 'hours_changed'; })
    .map(function (x) { return '<li style="font-size:13px;line-height:1.6">' + cb(x.emp_code, 'hours_changed', x.name) + who(x) + ' &middot; ' + val(x.from) + 'h &rarr; ' + val(x.to) + 'h</li>'; });
  const deactivated = changes.filter(function (x) { return x.category === 'deactivated'; })
    .map(function (x) { return '<li style="font-size:13px;line-height:1.6">' + cb(x.emp_code, 'deactivated', x.name) + who(x) + '</li>'; });
  const joiners = newJoiners.map(function (x) {
    const at = x.store != null ? (' &middot; ' + val(x.store)) : '';
    const rl = x.role != null ? (' &middot; ' + val(x.role)) : '';
    // Prominent per-joiner "needs PIN" warning — a joiner is created active with NO
    // PIN, so they cannot clock in until an admin sets one.
    const pinWarn = ' <span style="color:var(--ui-warn);font-weight:700">&#9888; needs PIN</span>';
    return '<li style="font-size:13px;line-height:1.6">' + cb(x.emp_code, 'new_joiner', x.name) + who(x) + at + rl + pinWarn + '</li>';
  });
  const unknown = unknownStores.map(function (x) {
    return '<li style="font-size:13px;line-height:1.6">' + who(x) + ' &middot; store code ' + val(x.store_code) + '</li>';
  });
  const ambig = ambiguous.map(function (x) {
    return '<li style="font-size:13px;line-height:1.6">emp_code ' + val(x.emp_code) + ' &middot; matches ' + (x.count || 0) + ' current staff</li>';
  });

  const chip = function (n, label, bg, col) {
    if (!n) return '';
    return '<span style="font-size:12px;background:' + bg + ';color:' + col + ';padding:3px 10px;border-radius:6px;font-weight:700">' + n + ' ' + escapeHtml(label) + '</span>';
  };
  const summary = '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px">' +
    chip(c.moved, 'moved', 'rgba(29,78,216,.12)', 'var(--info-deep)') +
    chip(c.name_changed, 'name', 'rgba(13,148,136,.14)', 'var(--ap-c-teal)') +
    chip(c.role_changed, 'role', 'rgba(124,58,237,.12)', 'var(--ap-c-violet)') +
    chip(c.hours_changed, 'hours', 'rgba(2,132,199,.12)', 'var(--ap-c-sky)') +
    chip(c.deactivated, 'left', 'rgba(255,92,122,.14)', 'var(--ui-bad)') +
    chip(c.new_joiner, 'joiners', 'rgba(46,230,166,.16)', 'var(--ap-teal-text)') +
    chip(c.unknown, 'unknown store', 'rgba(255,194,75,.16)', 'var(--ui-warn)') +
    chip(c.ambiguous, 'ambiguous', 'rgba(255,194,75,.16)', 'var(--ui-warn)') +
    (c.unchanged ? '<span style="font-size:12px;background:rgba(72,89,126,.14);color:var(--sub);padding:3px 10px;border-radius:6px;font-weight:700">' + c.unchanged + ' unchanged</span>' : '') +
    // v136.79: people an older push carried but the latest full list does not; never offered, never applied
    (c.not_in_latest ? '<span title="In an older push only. The latest full list from HR does not include them, so they are not offered." style="font-size:12px;background:rgba(72,89,126,.14);color:var(--sub);padding:3px 10px;border-radius:6px;font-weight:700">' + c.not_in_latest + ' ' + escapeHtml('no longer sent') + '</span>' : '') +
  '</div>';

  // The number of changes the APPLY will actually write (role is flag-only; unknown
  // stores + ambiguous emp_codes are skipped, so they don't gate the button).
  const applyableTotal = (c.moved || 0) + (c.name_changed || 0) + (c.hours_changed || 0) +
    (c.deactivated || 0) + (c.new_joiner || 0);

  // Store-move caution — shown only when there's a move to apply.
  const moveCaution = (c.moved ? (
    '<div style="font-size:12px;background:rgba(255,194,75,.14);border:1px solid rgba(255,194,75,.4);color:var(--warn-deep);padding:8px 10px;border-radius:8px;margin-bottom:10px;line-height:1.5">' +
      '&#9888; <strong>Store moves</strong> keep past/today shifts at the OLD store (protects worked pay history) and REMOVE future shifts from it; the staffer\'s store then flips to the new one. Cover shifts (someone else\'s store) are left untouched.' +
    '</div>'
  ) : '');

  // New-joiner caution — shown only when there's a joiner to create.
  const joinerCaution = (c.new_joiner ? (
    '<div style="font-size:12px;background:rgba(46,230,166,.12);border:1px solid rgba(46,230,166,.4);color:var(--ap-teal-text);padding:8px 10px;border-radius:8px;margin-bottom:10px;line-height:1.5">' +
      '&#9888; <strong>New joiners</strong> are created ACTIVE with NO PIN &mdash; they cannot clock in until an admin sets a PIN in their staff record.' +
    '</div>'
  ) : '');

  // Removal (leaver) caution — shown only when Amanah proposes a deactivation.
  const leaverCaution = (c.deactivated ? (
    '<div style="font-size:12px;background:rgba(255,92,122,.12);border:1px solid rgba(255,92,122,.45);color:var(--danger-deep);padding:8px 10px;border-radius:8px;margin-bottom:10px;line-height:1.5">' +
      '&#9888; <strong>Removals</strong> mark a staffer as having LEFT THE BUSINESS. Amanah can lag behind Shift &mdash; only confirm a removal if the person has genuinely left. Removals are NEVER part of &ldquo;Approve all safe updates&rdquo;; tick each and confirm by name.' +
    '</div>'
  ) : '');

  const applyBtn = (applyableTotal > 0 ? (
    '<div style="padding-top:10px;border-top:1px solid var(--border);margin-top:4px;display:flex;flex-direction:column;gap:8px">' +
      '<label style="font-size:12px;color:var(--sub);display:flex;align-items:center;gap:6px;cursor:pointer">' +
        '<input type="checkbox" id="amanahSelectAll" onchange="toggleAllStaffChanges(this)" style="vertical-align:middle">' +
        'Select all / none' +
      '</label>' +
      '<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">' +
        '<button class="btn btn-sm" style="background:#0a8a5f;color:#fff;font-weight:700" onclick="applySelectedStaffChanges()">&#9989; Approve selected (<span id="amanahSelCount">0</span>)</button>' +
        '<button class="btn btn-sm btn-outline" style="font-weight:700" onclick="applyStaffChanges()">Approve all safe updates</button>' +
        '<span style="font-size:11px;color:var(--sub)">&ldquo;Approve all safe updates&rdquo; applies name / hours changes + new joiners. Store moves &amp; removals are NEVER bulk-applied &mdash; tick each one and use &ldquo;Approve selected&rdquo; to confirm it by name. Role changes are never applied.</span>' +
      '</div>' +
    '</div>'
  ) : (
    '<div style="font-size:11px;color:var(--sub);padding-top:8px;border-top:1px solid var(--border);margin-top:2px">' +
      'Nothing to apply (role changes are flagged but never written; unknown/ambiguous rows are skipped).' +
    '</div>'
  ));

  // v131.21 (Al Reem Mall, 7 Sep): an UNKNOWN store code usually means a new
  // store is opening — Amanah starts sending its staff before anyone has
  // created the store in Shift. Until now the rows sat in "unknown store
  // (skipped)" and the link was an SQL-only step. Offer the fix right here:
  // create the store (prefilled modal, code linked on save) or link the code
  // to an existing store. Grouped by code — one banner per new store, not one
  // per staffer.
  const codeGroups = {};
  unknownStores.forEach(function (x) {
    const cc = x.store_code != null ? String(x.store_code).trim() : '';
    if (!cc) return;
    (codeGroups[cc] = codeGroups[cc] || []).push(x);
  });
  const storeOpts = (typeof getStoreMeta === 'function') ? Object.keys(getStoreMeta()).sort() : [];
  const newStoreBanners = Object.keys(codeGroups).map(function (code, idx) {
    const rows = codeGroups[code];
    const names = rows.map(function (r) { return r.name || r.emp_code || '?'; }).slice(0, 8).join(', ');
    const more = rows.length > 8 ? ' +' + (rows.length - 8) + ' more' : '';
    const opts = storeOpts.map(function (s) {
      return '<option translate="no" value="' + escapeHtml(s) + '">' + escapeHtml(s) + '</option>';
    }).join('');
    return '<div style="font-size:12.5px;background:rgba(255,194,75,.12);border:1px solid rgba(255,194,75,.5);color:var(--warn-deep);padding:10px 12px;border-radius:8px;margin-bottom:10px;line-height:1.6">' +
      '&#127381; <strong>Looks like a new store may be opening.</strong> Amanah is sending ' + rows.length +
      ' staff under a store code Shift does not know: <strong translate="no">' + escapeHtml(code) + '</strong>' +
      ' &mdash; <span translate="no">' + escapeHtml(names + more) + '</span>.' +
      '<div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">' +
        '<button class="btn btn-sm" style="background:#0a8a5f;color:#fff;font-weight:700" onclick="startCreateStoreForCode(\'' + escapeAttr(code) + '\')">&#10133; Create the store &amp; link this code</button>' +
        '<span style="font-size:11.5px">or it already exists in Shift:</span>' +
        '<select id="amanahLinkSel_' + idx + '" class="form-input" style="width:auto;font-size:12px;padding:4px 8px"><option value="">Pick a store&hellip;</option>' + opts + '</select>' +
        '<button class="btn btn-sm btn-outline" style="font-weight:700" onclick="linkCodeToStore(\'' + escapeAttr(code) + '\',' + idx + ')">&#128279; Link code</button>' +
      '</div>' +
      '<div style="margin-top:5px;font-size:11px">Once linked, this review refreshes and the staff above can be applied to the store. New stores also need a device password and staff PINs before anyone can clock in.</div>' +
    '</div>';
  }).join('');

  return summary +
    newStoreBanners +
    moveCaution +
    leaverCaution +
    joinerCaution +
    section('&#128260;', 'Moved store (tick to confirm — NOT in Approve all)', moved) +
    section('&#9997;', 'Name changed (will overwrite)', nameChanged) +
    section('&#127917;', 'Role changed (flagged only — NOT applied)', roleChanged) +
    section('&#9201;', 'Hours changed', hoursChanged) +
    section('&#10134;', 'Left the business (removal — tick to confirm)', deactivated) +
    section('&#10133;', 'New joiner (created active, needs PIN)', joiners) +
    section('&#9888;', 'Unknown store code (skipped)', unknown) +
    section('&#9888;', 'Ambiguous emp_code (skipped)', ambig) +
    applyBtn;
}

// v131.21 — the "new store from the feed" handlers. startCreateStoreForCode
// stashes the code on window (cross-bundle: src/uploads/stores.js consumes it
// after a successful create — same window-slot pattern as _storeEmailMap) and
// opens the existing Add Store modal; linkCodeToStore points the code at a
// store that already exists.
function startCreateStoreForCode(code) {
  if (typeof window.openAddStoreModal !== 'function') {
    showToast('Open Stores > + Add Store to create it first', 'error');
    return;
  }
  window._pendingStoreCodeLink = { code: String(code) };
  window.openAddStoreModal();
  const t = document.getElementById('storeModalTitle');
  if (t) t.textContent = 'Add Store — Amanah code ' + String(code) + ' will be linked on save';
}

async function linkCodeToStore(code, idx) {
  const sel = document.getElementById('amanahLinkSel_' + idx);
  const store = sel && sel.value;
  if (!store) { showToast('Pick the store to link the code to', 'error'); return; }
  try {
    await DB.stores.linkCode(String(code), store);
    showToast('Amanah code ' + code + ' linked to ' + store, 'success');
    loadStaffChanges();
  } catch (e) {
    showToast('Link failed: ' + ((e && e.message) || e), 'error');
  }
}

// v133.116: "Also manages" - the other stores a store manager looks after (Matt, 13 Oct 2026). Shown for a
// store manager only. Head Office see every store; an area manager sees their own area's stores (plus any
// already ticked, which only Head Office can remove). Saved on its own route by "Save stores": the general
// Save never carries it, so staff edits keep working before migration v237-0 runs.
function alsoManagesChoices(s) {
  const all = (typeof ALL_STORES !== 'undefined' && ALL_STORES) ? ALL_STORES : [];
  const isHO = typeof currentAdmin !== 'undefined' && currentAdmin && currentAdmin.role === 'admin';
  const mine = isHO ? all : ((typeof AREA_STORE_MAP !== 'undefined' && currentAdmin) ? coverAwareAreaStores(currentAdmin, AREA_STORE_MAP) : []);   // v136.9: + hand-overs
  const had = (s && s.alsoManages) || [];
  return [...new Set([...mine, ...had])]
    .filter(function (n) { return n && n !== s.store && n !== 'Retail Relief Managers'; })
    .sort();
}
function renderAlsoManages(s) {
  const group = document.getElementById('sfAlsoManagesGroup');
  const box = document.getElementById('sfAlsoManages');
  const msg = document.getElementById('sfAlsoManagesMsg');
  if (!group || !box) return;
  const isManager = s && (s.role === 'store_manager' || s.role === 'retail_relief_manager');
  const had = (s && s.alsoManages) || [];
  // A non-manager with stores left over (a demotion) still shows them, so they can be cleared.
  group.style.display = (isManager || had.length) ? '' : 'none';
  if (msg) { msg.textContent = ''; msg.style.color = ''; }
  box.innerHTML = alsoManagesChoices(s).map(function (n) {
    const on = had.indexOf(n) !== -1;
    return '<label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer">' +
      '<input type="checkbox" class="sf-also-manages" value="' + escapeHtml(n) + '"' + (on ? ' checked' : '') + '> <span translate="no">' + escapeHtml(n) + '</span></label>';
  }).join('') || '<span style="font-size:13px;color:var(--tiny)">No other stores to choose from.</span>';
}
async function saveAlsoManages() {
  if (!editingStaffId) return;
  const s = staffData.find(function (x) { return x.id === editingStaffId; });
  if (!s) return;
  const stores = Array.from(document.querySelectorAll('.sf-also-manages:checked')).map(function (b) { return b.value; });
  const btn = document.getElementById('sfAlsoManagesSave');
  const msg = document.getElementById('sfAlsoManagesMsg');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    const res = await DB.staff.setAlsoManages(editingStaffId, stores);
    s.alsoManages = (res && res.also_manages) || [];
    renderAlsoManages(s);
    const m = document.getElementById('sfAlsoManagesMsg');
    if (m) { m.textContent = s.alsoManages.length ? 'Saved: also manages ' + s.alsoManages.join(', ') : 'Saved: home store only'; m.style.color = 'var(--green)'; }
    showToast('Stores saved for ' + s.name, 'success');
  } catch (e) {
    if (msg) { msg.textContent = 'Not saved: ' + ((e && e.message) || e); msg.style.color = 'var(--red)'; }
    showToast('Stores NOT saved: ' + ((e && e.message) || e), 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save stores'; }
  }
}

window.renderAlsoManages = renderAlsoManages;
window.saveAlsoManages = saveAlsoManages;
window.staffRowHtml = staffRowHtml;
window.loadStaffChanges = loadStaffChanges;
window.startCreateStoreForCode = startCreateStoreForCode;
window.linkCodeToStore = linkCodeToStore;
window.applyStaffChanges = applyStaffChanges;
window.applySelectedStaffChanges = applySelectedStaffChanges;
window.toggleAllStaffChanges = toggleAllStaffChanges;
window.updateAmanahSelectedCount = updateAmanahSelectedCount;
window.revealCurrentPin = revealCurrentPin;
window.hideRevealedPin = hideRevealedPin;
window.resetThisStaffConsent = resetThisStaffConsent;
window.persistStaffData = persistStaffData;
window.openAddStaffModal = openAddStaffModal;
window.closeAddStaffModal = closeAddStaffModal;
window.updateStoreDropdown = updateStoreDropdown;
window.updateNewStoreDropdown = updateNewStoreDropdown;
window.renderChangelog = renderChangelog;
window.cancelChangeById = cancelChangeById;
window.renderRequests = renderRequests;
window.openReqModal = openReqModal;
window.closeReqModal = closeReqModal;
window.saveRequest = saveRequest;
